import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Circle, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Activity,
  Bookmark,
  BookmarkPlus,
  BrainCircuit,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  Gauge,
  GitBranch,
  Globe2,
  Layers,
  Maximize2,
  Monitor,
  Navigation,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_BASE_URL as BASE_URL } from '../config';

const OSINT_CSS = `
@keyframes osintRingA {
  0%   { transform: translate(-50%,-50%) scale(0.8); opacity: 0.85; }
  100% { transform: translate(-50%,-50%) scale(3.6); opacity: 0;    }
}
@keyframes osintRingB {
  0%   { transform: translate(-50%,-50%) scale(0.8); opacity: 0.6;  }
  100% { transform: translate(-50%,-50%) scale(2.7); opacity: 0;    }
}
@keyframes osintRingC {
  0%   { transform: translate(-50%,-50%) scale(0.8); opacity: 0.4;  }
  100% { transform: translate(-50%,-50%) scale(2.0); opacity: 0;    }
}
@keyframes osintGlow {
  0%,100% { filter: brightness(1)   drop-shadow(0 0 4px var(--oc)); }
  50%     { filter: brightness(1.4) drop-shadow(0 0 10px var(--oc)); }
}
@keyframes osintScan {
  0%   { top: -2px; opacity: 0;   }
  8%   { opacity: 0.55; }
  92%  { opacity: 0.55; }
  100% { top: 100%;  opacity: 0;  }
}
.osint-ra { animation: osintRingA var(--ospd,2.2s) ease-out infinite; }
.osint-rb { animation: osintRingB var(--ospd,2.2s) ease-out calc(var(--ospd,2.2s) * 0.3) infinite; }
.osint-rc { animation: osintRingC var(--ospd,2.2s) ease-out calc(var(--ospd,2.2s) * 0.6) infinite; }
.osint-core { animation: osintGlow 2s ease-in-out infinite; }
.osint-scan { animation: osintScan 5s linear infinite; }
.osint-globe-mode .leaflet-map-pane {
  transform-origin: center 58%;
  transition: transform 700ms ease;
}
.osint-tilt-equator.osint-globe-mode .leaflet-map-pane {
  transform: perspective(1200px) rotateX(21deg) scale(1.06) translateY(-8px) !important;
}
.osint-tilt-birds-eye.osint-globe-mode .leaflet-map-pane {
  transform: perspective(1200px) rotateX(42deg) scale(1.12) translateY(-18px) !important;
}
.osint-tilt-polar.osint-globe-mode .leaflet-map-pane {
  transform: perspective(1200px) rotateX(7deg) scale(1.02) translateY(-2px) !important;
}
@keyframes osintAutoSpin {
  from { --spin-x: 0deg; }
  to   { --spin-x: 360deg; }
}
@keyframes osintFlightDash {
  0%   { stroke-dashoffset: 800; opacity: 0.3; }
  30%  { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.85; }
}
.osint-flight-arc {
  animation: osintFlightDash 2s ease-in-out forwards;
}
.osint-globe-vignette {
  background:
    radial-gradient(circle at 50% 110%, rgba(34,211,238,0.28), transparent 55%),
    radial-gradient(circle at 14% 20%, rgba(6,182,212,0.12), transparent 50%),
    radial-gradient(circle at 88% 16%, rgba(244,63,94,0.12), transparent 45%);
}
`;

type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM';
type TimeFilter = 'LIVE' | '1H' | '24H' | '7D';
type MapMode = 'POINTS' | 'HEAT';
type PanelTab = 'OVERVIEW' | 'NARRATIVES' | 'LINKED' | 'VELOCITY' | 'IMPACT';

type LayerKey =
  | 'misinfo'
  | 'cyber'
  | 'identity'
  | 'transport'
  | 'forensics'
  | 'neural';

type SizeMode = 'POSTS' | 'RISK';
type GlobeTilt = 'EQUATOR' | 'BIRDS_EYE' | 'POLAR';

interface SavedPreset {
  name: string;
  layers: LayerKey[];
  threshold: number;
}

interface VelocityPoint {
  t: string;
  risk: number;
}

interface GeoLocation {
  id: string;
  name: string;
  lat: number;
  lng: number;
  confidence: number;
  severity: Severity;
  posts: number;
  region: string;
  avg_risk?: number;
  escalating?: boolean;
  actors: string[];
  narrativeClusters: string[];
  linkedSignals: string[];
  projectedImpact: string;
  velocityTrend: VelocityPoint[];
  layers: LayerKey[];
}

const FALLBACK_HOTSPOTS: GeoLocation[] = [
  {
    id: 'delhi',
    name: 'Delhi',
    lat: 28.6139,
    lng: 77.209,
    confidence: 0.9,
    severity: 'CRITICAL',
    posts: 1842,
    region: 'South Asia',
    avg_risk: 0.89,
    escalating: true,
    actors: ['Proxy Amplifier Net', 'Election Pulse Cells'],
    narrativeClusters: ['Institution capture', 'Polling fraud allegations', 'Civil unrest baiting'],
    linkedSignals: ['3x bot swarm spike', 'Cross-platform meme template reuse', 'Sudden low-trust hashtag drift'],
    projectedImpact: 'Escalation to multi-city legitimacy protests within 48h if narrative velocity remains above +18%.',
    velocityTrend: [
      { t: 'T-6', risk: 63 },
      { t: 'T-5', risk: 66 },
      { t: 'T-4', risk: 69 },
      { t: 'T-3', risk: 74 },
      { t: 'T-2', risk: 81 },
      { t: 'T-1', risk: 87 },
    ],
    layers: ['misinfo', 'identity', 'forensics', 'neural'],
  },
  {
    id: 'new-york',
    name: 'New York',
    lat: 40.7128,
    lng: -74.006,
    confidence: 0.8,
    severity: 'HIGH',
    posts: 1203,
    region: 'North America',
    avg_risk: 0.72,
    escalating: false,
    actors: ['Synthetic News Relay', 'Influencer Cluster 9B'],
    narrativeClusters: ['Public safety panic', 'Banking instability whisper campaign'],
    linkedSignals: ['Geo-targeted ad bursts', 'Narrative crossover from encrypted channels'],
    projectedImpact: 'Likely market sentiment turbulence in 24h; reputational risk for municipal institutions.',
    velocityTrend: [
      { t: 'T-6', risk: 47 },
      { t: 'T-5', risk: 53 },
      { t: 'T-4', risk: 56 },
      { t: 'T-3', risk: 59 },
      { t: 'T-2', risk: 61 },
      { t: 'T-1', risk: 64 },
    ],
    layers: ['misinfo', 'cyber', 'transport'],
  },
  {
    id: 'london',
    name: 'London',
    lat: 51.5074,
    lng: -0.1278,
    confidence: 0.95,
    severity: 'CRITICAL',
    posts: 2105,
    region: 'Europe',
    avg_risk: 0.92,
    escalating: true,
    actors: ['Identity Laundering Mesh', 'Narrative Seeder Nodes'],
    narrativeClusters: ['Election integrity erosion', 'Financial sabotage framing', 'State response delegitimization'],
    linkedSignals: ['Deepfake voice clip recirculation', 'Coordinated repost cadence', 'Platform moderation evasion'],
    projectedImpact: 'High probability of transatlantic narrative export to allied media corridors.',
    velocityTrend: [
      { t: 'T-6', risk: 68 },
      { t: 'T-5', risk: 71 },
      { t: 'T-4', risk: 76 },
      { t: 'T-3', risk: 83 },
      { t: 'T-2', risk: 88 },
      { t: 'T-1', risk: 93 },
    ],
    layers: ['misinfo', 'identity', 'forensics', 'cyber'],
  },
  {
    id: 'moscow',
    name: 'Moscow',
    lat: 55.7558,
    lng: 37.6173,
    confidence: 0.7,
    severity: 'HIGH',
    posts: 891,
    region: 'Eastern Europe',
    avg_risk: 0.69,
    escalating: false,
    actors: ['Hybrid Influence Unit', 'Bot Theatre Ring'],
    narrativeClusters: ['Energy weaponization claims', 'Military logistics rumor chain'],
    linkedSignals: ['Cross-language misinformation bridge', 'Sudden actor account churn'],
    projectedImpact: 'Potential spillover into neighboring political discourse channels over 72h.',
    velocityTrend: [
      { t: 'T-6', risk: 45 },
      { t: 'T-5', risk: 52 },
      { t: 'T-4', risk: 57 },
      { t: 'T-3', risk: 61 },
      { t: 'T-2', risk: 62 },
      { t: 'T-1', risk: 63 },
    ],
    layers: ['misinfo', 'cyber', 'transport'],
  },
  {
    id: 'beijing',
    name: 'Beijing',
    lat: 39.9042,
    lng: 116.4074,
    confidence: 0.85,
    severity: 'CRITICAL',
    posts: 1654,
    region: 'East Asia',
    avg_risk: 0.84,
    escalating: true,
    actors: ['Narrative Centrifuge Group', 'Persona Farm Wing'],
    narrativeClusters: ['Trade corridor instability', 'AI-generated testimony clips'],
    linkedSignals: ['Synthetic profile swarms', 'State-policy framing pivot'],
    projectedImpact: 'Expected narrative harmonization across regional language clusters by next cycle.',
    velocityTrend: [
      { t: 'T-6', risk: 59 },
      { t: 'T-5', risk: 63 },
      { t: 'T-4', risk: 67 },
      { t: 'T-3', risk: 73 },
      { t: 'T-2', risk: 79 },
      { t: 'T-1', risk: 84 },
    ],
    layers: ['misinfo', 'identity', 'neural', 'forensics'],
  },
  {
    id: 'sao-paulo',
    name: 'Sao Paulo',
    lat: -23.5505,
    lng: -46.6333,
    confidence: 0.65,
    severity: 'MEDIUM',
    posts: 427,
    region: 'South America',
    avg_risk: 0.52,
    escalating: false,
    actors: ['Election Meme Syndicate'],
    narrativeClusters: ['Electoral machine rumors', 'Community trust dilution'],
    linkedSignals: ['Localized caption farming', 'Sentiment flash spikes'],
    projectedImpact: 'Manageable with coordinated debunk amplification and influencer correction.',
    velocityTrend: [
      { t: 'T-6', risk: 31 },
      { t: 'T-5', risk: 36 },
      { t: 'T-4', risk: 40 },
      { t: 'T-3', risk: 42 },
      { t: 'T-2', risk: 46 },
      { t: 'T-1', risk: 49 },
    ],
    layers: ['misinfo', 'identity'],
  },
  {
    id: 'lagos',
    name: 'Lagos',
    lat: 6.5244,
    lng: 3.3792,
    confidence: 0.6,
    severity: 'MEDIUM',
    posts: 312,
    region: 'West Africa',
    avg_risk: 0.5,
    escalating: true,
    actors: ['Election Integrity Ring', 'Narrative Booster Cells'],
    narrativeClusters: ['Election integrity challenge', 'Regional instability framing'],
    linkedSignals: ['92% narrative correlation with election-integrity cluster', 'Language-variant prompt seeding'],
    projectedImpact: 'Escalation to HIGH likely inside 12h if cross-border mention rate stays above baseline +40%.',
    velocityTrend: [
      { t: 'T-6', risk: 29 },
      { t: 'T-5', risk: 34 },
      { t: 'T-4', risk: 39 },
      { t: 'T-3', risk: 43 },
      { t: 'T-2', risk: 47 },
      { t: 'T-1', risk: 56 },
    ],
    layers: ['misinfo', 'identity', 'transport'],
  },
  {
    id: 'tehran',
    name: 'Tehran',
    lat: 35.6892,
    lng: 51.389,
    confidence: 0.75,
    severity: 'HIGH',
    posts: 703,
    region: 'Middle East',
    avg_risk: 0.67,
    escalating: false,
    actors: ['Persona Forge Unit', 'Cyber Threat Broker'],
    narrativeClusters: ['Infrastructure sabotage narrative', 'Identity leak fear-messaging'],
    linkedSignals: ['Credential dump references', 'Telegram narrative relay'],
    projectedImpact: 'Elevated cyber-physical narrative fusion risk over next 24h.',
    velocityTrend: [
      { t: 'T-6', risk: 44 },
      { t: 'T-5', risk: 48 },
      { t: 'T-4', risk: 53 },
      { t: 'T-3', risk: 58 },
      { t: 'T-2', risk: 60 },
      { t: 'T-1', risk: 62 },
    ],
    layers: ['cyber', 'identity', 'forensics'],
  },
  {
    id: 'dhaka',
    name: 'Dhaka',
    lat: 23.8103,
    lng: 90.4125,
    confidence: 0.82,
    severity: 'CRITICAL',
    posts: 988,
    region: 'South Asia',
    avg_risk: 0.79,
    escalating: true,
    actors: ['Flash-Mob Narrative Node', 'Synthetic Witness Factory'],
    narrativeClusters: ['Humanitarian misinformation storm', 'Relief distribution trust collapse'],
    linkedSignals: ['Emergency keyword hijack', 'Image-forensics mismatch'],
    projectedImpact: 'High probability of aid-route disruptions due to trust shock events.',
    velocityTrend: [
      { t: 'T-6', risk: 51 },
      { t: 'T-5', risk: 55 },
      { t: 'T-4', risk: 61 },
      { t: 'T-3', risk: 69 },
      { t: 'T-2', risk: 73 },
      { t: 'T-1', risk: 78 },
    ],
    layers: ['misinfo', 'transport', 'forensics', 'neural'],
  },
];

const SEV: Record<Severity, { color: string; speed: string; coreSize: number; rings: number }> = {
  MEDIUM: { color: '#06b6d4', speed: '3.0s', coreSize: 8, rings: 2 },
  HIGH: { color: '#f59e0b', speed: '2.4s', coreSize: 11, rings: 2 },
  CRITICAL: { color: '#f43f5e', speed: '1.8s', coreSize: 14, rings: 3 },
};

const LAYER_CONFIG: Array<{ key: LayerKey; label: string; color: string }> = [
  { key: 'misinfo', label: 'Misinfo Narratives', color: '#f43f5e' },
  { key: 'cyber', label: 'Cyber Threats', color: '#f59e0b' },
  { key: 'identity', label: 'Identity/Actors', color: '#06b6d4' },
  { key: 'transport', label: 'Geo-Transport', color: '#22c55e' },
  { key: 'forensics', label: 'Media Forensics', color: '#a78bfa' },
  { key: 'neural', label: 'Neural AI', color: '#ec4899' },
];

const TABS: PanelTab[] = ['OVERVIEW', 'NARRATIVES', 'LINKED', 'VELOCITY', 'IMPACT'];

const TIME_MULTIPLIER: Record<TimeFilter, number> = {
  LIVE: 1,
  '1H': 0.58,
  '24H': 1,
  '7D': 2.4,
};

const tabLabel = (tab: PanelTab): string => {
  switch (tab) {
    case 'OVERVIEW':
      return 'Overview';
    case 'NARRATIVES':
      return 'Narrative Clusters';
    case 'LINKED':
      return 'Linked Signals';
    case 'VELOCITY':
      return 'Velocity Trend';
    case 'IMPACT':
      return 'Projected Impact';
    default:
      return tab;
  }
};

const severityFromRisk = (risk: number): Severity => {
  if (risk >= 80) return 'CRITICAL';
  if (risk >= 60) return 'HIGH';
  return 'MEDIUM';
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const colorForRisk = (risk: number): string => {
  if (risk >= 82) return '#f43f5e';
  if (risk >= 68) return '#f59e0b';
  if (risk >= 50) return '#22d3ee';
  return '#3b82f6';
};

const hash = (text: string): number =>
  text.split('').reduce((acc, ch) => (acc * 33 + ch.charCodeAt(0)) % 1000, 7);

const toDisplayTime = (date: Date): string => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  }).formatToParts(date);
  const h = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const m = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const s = parts.find((p) => p.type === 'second')?.value ?? '00';
  return `${h}:${m}:${s} IST`;
};

const makeIcon = (severity: Severity, confidence: number, risk: number): L.DivIcon => {
  const { color, speed, coreSize, rings } = SEV[severity];
  const s = coreSize + Math.round(confidence * 4);
  const container = s * 6;
  const ringClasses = ['osint-ra', 'osint-rb', 'osint-rc'];
  const ringSizes = [s * 2.6, s * 2.0, s * 1.5];
  const speedMultiplier = clamp((100 - risk) / 120 + 0.55, 0.55, 1.1);

  const ringHtml = Array.from({ length: rings }, (_, i) => `
    <div class="${ringClasses[i]}"
      style="position:absolute;top:50%;left:50%;
             width:${ringSizes[i]}px;height:${ringSizes[i]}px;
             border:1.5px solid ${color};border-radius:50%;
             --ospd:calc(${speed} * ${speedMultiplier});">
    </div>`).join('');

  const html = `
    <div style="position:relative;width:${container}px;height:${container}px;">
      ${ringHtml}
      <div class="osint-core"
        style="position:absolute;top:50%;left:50%;
               transform:translate(-50%,-50%);
               width:${s}px;height:${s}px;
               background:${color};border-radius:50%;
               --oc:${color};
               box-shadow:0 0 ${s}px ${color}aa,0 0 ${s * 2}px ${color}44;">
      </div>
    </div>`;

  return L.divIcon({
    className: '',
    html,
    iconSize:   [container, container],
    iconAnchor: [container / 2, container / 2],
    tooltipAnchor: [s, -s],
  });
};

/** Leaflet child component: auto-pans the map to simulate globe rotation */
const AutoRotateController: React.FC<{ enabled: boolean; speed: number }> = ({ enabled, speed }) => {
  const map = useMap();
  const frameRef = useRef<number | null>(null);
  const lastRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      return;
    }
    const step = (ts: number) => {
      const delta = ts - lastRef.current;
      if (delta > 80) {
        lastRef.current = ts;
        const center = map.getCenter();
        map.setView([center.lat, center.lng + speed * 0.18], map.getZoom(), { animate: false });
      }
      frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [enabled, speed, map]);

  return null;
};

export const OSINTMap: React.FC = () => {
  const [hotspots, setHotspots] = useState<GeoLocation[]>([]);
  const [isLive, setIsLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mapMode, setMapMode] = useState<MapMode>('POINTS');
  const [globeMode, setGlobeMode] = useState(false);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('LIVE');
  const [riskThreshold, setRiskThreshold] = useState(40);
  const [query, setQuery] = useState('');
  const [activeLayers, setActiveLayers] = useState<LayerKey[]>(LAYER_CONFIG.map((layer) => layer.key));
  const [selectedId, setSelectedId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<PanelTab>('OVERVIEW');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [insightIndex, setInsightIndex] = useState(0);
  const [compareCities, setCompareCities] = useState<string[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // ── New state: layer controls
  const [sizeMode, setSizeMode] = useState<SizeMode>('POSTS');
  const [crossPlatform, setCrossPlatform] = useState(false);
  const [layerPanelOpen, setLayerPanelOpen] = useState(true);
  const [savedPresets, setSavedPresets] = useState<SavedPreset[]>([
    { name: 'Election Monitoring', layers: ['misinfo', 'identity', 'neural'], threshold: 50 },
    { name: 'Health Crisis', layers: ['misinfo', 'transport', 'forensics'], threshold: 40 },
    { name: 'Cyber Incident', layers: ['cyber', 'identity', 'forensics'], threshold: 55 },
  ]);
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);

  // ── New state: globe controls
  const [autoRotate, setAutoRotate] = useState(false);
  const [rotateSpeed, setRotateSpeed] = useState(2);
  const [globeTilt, setGlobeTilt] = useState<GlobeTilt>('EQUATOR');
  const [globePanelOpen, setGlobePanelOpen] = useState(false);
  const [flightMode, setFlightMode] = useState(false);
  const [flightCities, setFlightCities] = useState<string[]>([]);
  const [heatExpanded, setHeatExpanded] = useState(false);
  const [propExpanded, setPropExpanded] = useState(false);

  const applyLiveVariation = (dataset: GeoLocation[], epochMs: number, multiplier: number): GeoLocation[] =>
    dataset.map((spot) => {
      const seed = hash(`${spot.id}-${Math.floor(epochMs / 30000)}-${timeFilter}`);
      const fluctuation = (seed % 14) - 7;
      const adjustedPosts = Math.max(1, Math.round(spot.posts * multiplier + fluctuation));
      const baselineRisk = (spot.avg_risk ?? spot.confidence) * 100;
      const adjustedRisk = clamp(baselineRisk + fluctuation * 0.9, 28, 98);
      const severity = severityFromRisk(adjustedRisk);

      return {
        ...spot,
        posts: adjustedPosts,
        avg_risk: adjustedRisk / 100,
        severity,
        escalating: adjustedRisk > baselineRisk + 4,
      };
    });

  const normalizeIncoming = (incoming: GeoLocation[]): GeoLocation[] => {
    if (!Array.isArray(incoming) || incoming.length === 0) {
      return [];
    }

    return incoming.map((item, index) => {
      const fallback = FALLBACK_HOTSPOTS[index % FALLBACK_HOTSPOTS.length];
      // Use || (not ??) so that avg_risk:0 from the API falls through to the
      // fallback baseline instead of being treated as a valid zero risk score.
      const risk = clamp(
        (item.avg_risk || item.confidence || fallback.avg_risk || fallback.confidence) * 100,
        45, // minimum 45 keeps every normalised hotspot above the default 40% threshold
        98,
      );
      return {
        ...fallback,
        id: item.name ? item.name.toLowerCase().replace(/\s+/g, '-') : fallback.id,
        name: item.name ?? fallback.name,
        lat: item.lat ?? fallback.lat,
        lng: item.lng ?? fallback.lng,
        region: item.region ?? fallback.region,
        posts: item.posts ?? fallback.posts,
        confidence: item.confidence ?? fallback.confidence,
        avg_risk: risk / 100,
        severity: severityFromRisk(risk),
      };
    });
  };

  useEffect(() => {
    let mounted = true;

    const refresh = async () => {
      const controller = new AbortController();
      try {
        const response = await fetch(`${BASE_URL}/api/analytics/geo-posts`, { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const data = (await response.json()) as GeoLocation[];
        const totalPosts = data.reduce((sum, hotspot) => sum + (hotspot.posts || 0), 0);
        if (mounted && data.length > 0 && totalPosts > 0) {
          setHotspots(normalizeIncoming(data));
          setIsLive(true);
          setLastUpdated(new Date());
        } else if (mounted) {
          // Live API responded but provided no usable hotspots.
          // Keep a guarded fallback only when we have no previous data.
          setHotspots((prev) => (prev.length > 0 ? prev : FALLBACK_HOTSPOTS));
          setIsLive(false);
          setLastUpdated(new Date());
        }
      } catch {
        if (mounted) {
          // On fetch failure, preserve existing live data if available and only
          // use fallback data when the map has no dataset yet.
          setHotspots((prev) => (prev.length > 0 ? prev : FALLBACK_HOTSPOTS));
          setIsLive(false);
          setLastUpdated(new Date());
        }
      } finally {
        if (mounted) setLoading(false);
      }
      return () => controller.abort();
    };

    refresh();
    const interval = window.setInterval(() => {
      refresh();
      setInsightIndex((current) => (current + 1) % 4);
      setLastUpdated(new Date());
    }, 30000);

    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (document.querySelector('[data-osint-css]')) return;
    const el = document.createElement('style');
    el.setAttribute('data-osint-css', '1');
    el.textContent = OSINT_CSS;
    document.head.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);

  // Keyboard shortcuts: G = toggle 3D globe, L = toggle layer panel
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'g' || e.key === 'G') setGlobeMode((v) => !v);
      if (e.key === 'l' || e.key === 'L') setLayerPanelOpen((v) => !v);
      if (e.key === 'Escape') { setFlightMode(false); setFlightCities([]); setHeatExpanded(false); setPropExpanded(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const multiplier = TIME_MULTIPLIER[timeFilter];

  const timelineHotspots = useMemo(
    () => applyLiveVariation(hotspots, lastUpdated.getTime(), multiplier),
    [hotspots, lastUpdated, multiplier],
  );

  const visibleHotspots = useMemo(() => {
    const q = query.trim().toLowerCase();
    return timelineHotspots
      .filter((spot) => {
        const riskScore = (spot.avg_risk ?? spot.confidence) * 100;
        if (riskScore < riskThreshold) return false;
        if (!spot.layers.some((layer) => activeLayers.includes(layer))) return false;
        if (!q) return true;

        return (
          spot.name.toLowerCase().includes(q) ||
          spot.region.toLowerCase().includes(q) ||
          spot.narrativeClusters.some((item) => item.toLowerCase().includes(q)) ||
          spot.actors.some((item) => item.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => (b.avg_risk ?? b.confidence) - (a.avg_risk ?? a.confidence));
  }, [timelineHotspots, query, riskThreshold, activeLayers]);

  // Count visible hotspots per layer (for badges on layer chips)
  const layerHotspotCount = useMemo(() => {
    const counts: Record<LayerKey, number> = { misinfo: 0, cyber: 0, identity: 0, transport: 0, forensics: 0, neural: 0 };
    timelineHotspots.forEach((spot) => {
      const riskScore = (spot.avg_risk ?? spot.confidence) * 100;
      if (riskScore < riskThreshold) return;
      spot.layers.forEach((l) => { counts[l] += 1; });
    });
    return counts;
  }, [timelineHotspots, riskThreshold]);

  // Compute flight arc between selected flightCities
  const flightArc = useMemo(() => {
    if (flightCities.length < 2) return null;
    const a = timelineHotspots.find((s) => s.id === flightCities[0]);
    const b = timelineHotspots.find((s) => s.id === flightCities[1]);
    if (!a || !b) return null;
    return { from: [a.lat, a.lng] as [number, number], to: [b.lat, b.lng] as [number, number], fromName: a.name, toName: b.name };
  }, [flightCities, timelineHotspots]);

  const markers = useMemo(
    () => visibleHotspots.map((spot) => ({ ...spot, icon: makeIcon(spot.severity, spot.confidence, (spot.avg_risk ?? spot.confidence) * 100) })),
    [visibleHotspots],
  );

  const selectedHotspot = useMemo(
    () => visibleHotspots.find((spot) => spot.id === selectedId) || visibleHotspots[0] || null,
    [visibleHotspots, selectedId],
  );

  const aiInsights = useMemo(() => {
    const lagos = visibleHotspots.find((spot) => spot.name === 'Lagos');
    const score = lagos ? Math.round((lagos.avg_risk ?? lagos.confidence) * 100) : 92;
    return [
      `Lagos cluster shows ${score}% correlation with election integrity narrative from Narrative Mapping.`,
      'London-Delhi propagation arc is currently the fastest cross-region narrative corridor in this cycle.',
      'Identity/Actor overlays indicate synchronized persona reuse across 3 geopolitical language groups.',
      'Neural AI signals are concentrated in media-forensics anomalies around critical civic events.',
    ];
  }, [visibleHotspots]);

  const propagationLines = useMemo(() => {
    const candidates = visibleHotspots
      .filter((spot) => spot.severity !== 'MEDIUM')
      .slice(0, 6);
    const lines: Array<{ from: [number, number]; to: [number, number]; color: string; weight: number }> = [];

    for (let i = 0; i < candidates.length - 1; i += 1) {
      const a = candidates[i];
      const b = candidates[i + 1];
      const maxRisk = Math.max((a.avg_risk ?? a.confidence) * 100, (b.avg_risk ?? b.confidence) * 100);
      lines.push({
        from: [a.lat, a.lng],
        to: [b.lat, b.lng],
        color: colorForRisk(maxRisk),
        weight: maxRisk > 80 ? 3 : 2,
      });
    }
    return lines;
  }, [visibleHotspots]);

  const totalThreats = visibleHotspots.reduce((sum, spot) => sum + spot.posts, 0);
  const escalatingCount = visibleHotspots.filter((spot) => spot.escalating).length;
  const weightedRisk =
    visibleHotspots.reduce((sum, spot) => sum + (spot.avg_risk ?? spot.confidence) * 100, 0) /
    Math.max(1, visibleHotspots.length);
  const globalRiskScore = Math.round(weightedRisk);

  const toggleLayer = (layer: LayerKey) => {
    setActiveLayers((current) => {
      if (current.includes(layer)) {
        const reduced = current.filter((item) => item !== layer);
        return reduced.length === 0 ? current : reduced;
      }
      return [...current, layer];
    });
  };

  const resetAllLayers = () => setActiveLayers(LAYER_CONFIG.map((l) => l.key));

  const applyPreset = (preset: SavedPreset) => {
    setActiveLayers(preset.layers);
    setRiskThreshold(preset.threshold);
  };

  const saveCurrentPreset = () => {
    const name = presetName.trim();
    if (!name) return;
    setSavedPresets((prev) => [...prev.filter((p) => p.name !== name), { name, layers: [...activeLayers], threshold: riskThreshold }]);
    setPresetName('');
    setSavingPreset(false);
  };

  const removePreset = (name: string) => {
    setSavedPresets((prev) => prev.filter((p) => p.name !== name));
  };

  const handleFlightClick = (id: string) => {
    if (!flightMode) return;
    setFlightCities((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id].slice(-2);
      return next;
    });
  };

  const onAddCompare = (id: string) => {
    setCompareCities((current) => {
      if (current.includes(id)) return current;
      return [...current, id].slice(-4);
    });
  };

  const onRemoveCompare = (id: string) => {
    setCompareCities((current) => current.filter((item) => item !== id));
  };

  const onDropCompare = (targetIndex: number) => {
    if (dragIndex == null || dragIndex === targetIndex) return;
    setCompareCities((current) => {
      const arr = [...current];
      const [moved] = arr.splice(dragIndex, 1);
      arr.splice(targetIndex, 0, moved);
      return arr;
    });
    setDragIndex(null);
  };

  const exportHeatmapSnapshot = () => {
    const payload = {
      exportedAt: new Date().toISOString(),
      mapMode,
      globeMode,
      timeFilter,
      riskThreshold,
      activeLayers,
      globalRiskScore,
      hotspots: visibleHotspots.map((spot) => ({
        name: spot.name,
        region: spot.region,
        severity: spot.severity,
        posts: spot.posts,
        riskScore: Math.round((spot.avg_risk ?? spot.confidence) * 100),
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `osint-heatmap-snapshot-${Date.now()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`h-full w-full rounded-xl overflow-hidden border border-zinc-800/50 shadow-lg relative bg-zinc-950 ${globeMode ? `osint-globe-mode osint-tilt-${globeTilt === 'BIRDS_EYE' ? 'birds-eye' : globeTilt === 'POLAR' ? 'polar' : 'equator'}` : ''}`}>
      <div className="osint-globe-vignette absolute inset-0 z-[200] pointer-events-none" />

      {/* Floating left-edge controls — heatmap expand + propagation tree */}
      <div className="absolute left-3 top-1/2 -translate-y-1/2 z-[480] flex flex-col gap-2">
        {/* Expand heatmap */}
        <button
          type="button"
          onMouseEnter={() => setHeatExpanded(true)}
          onClick={() => setHeatExpanded(true)}
          title="Expand heatmap view"
          className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl border border-cyan-500/40 bg-zinc-950/90 backdrop-blur-sm text-cyan-300 hover:border-cyan-400/80 hover:bg-cyan-950/90 hover:text-cyan-100 transition-all shadow-[0_0_16px_rgba(6,182,212,0.25)]"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          <span className="text-xs font-medium tracking-wide [writing-mode:vertical-rl] rotate-180 leading-none">Expand</span>
        </button>
        {/* Expand node propagation tree */}
        <button
          type="button"
          onMouseEnter={() => setPropExpanded(true)}
          onClick={() => setPropExpanded(true)}
          title="Expand node propagation tree"
          className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl border border-violet-500/40 bg-zinc-950/90 backdrop-blur-sm text-violet-300 hover:border-violet-400/80 hover:bg-violet-950/90 hover:text-violet-100 transition-all shadow-[0_0_16px_rgba(139,92,246,0.25)]"
        >
          <GitBranch className="h-3.5 w-3.5" />
          <span className="text-xs font-medium tracking-wide [writing-mode:vertical-rl] rotate-180 leading-none">Tree</span>
        </button>
      </div>

      <div className="absolute left-3 right-3 top-3 z-[450] flex flex-col gap-2">
        <div className="rounded-lg border border-zinc-700/60 bg-zinc-900/90 backdrop-blur-md px-3 py-2 text-[11px] text-zinc-200 shadow-xl">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-semibold tracking-wide">
              <Gauge className="h-3.5 w-3.5 text-rose-300" />
              Platform-Wide Misinfo Pressure
            </div>
            <div className="flex items-center gap-2 text-zinc-300">
              <span className="rounded-full border border-rose-500/30 bg-rose-500/15 px-2 py-0.5 text-rose-200">
                Global Risk Score: {globalRiskScore}/100
              </span>
              <span className="rounded-full border border-amber-500/25 bg-amber-500/12 px-2 py-0.5 text-amber-100">
                {escalatingCount} hotspots escalating
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-700/60 bg-zinc-900/85 backdrop-blur-sm px-3 py-2">
          <div className="flex items-center gap-2">
            <Search className="h-3.5 w-3.5 text-cyan-300" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search city, narrative, or actor"
              className="w-full bg-zinc-950/80 text-zinc-100 placeholder:text-zinc-400 outline-none px-2 py-1 rounded border border-zinc-700/60 text-[11px]"
            />
            <button
              onClick={exportHeatmapSnapshot}
              className="flex items-center gap-1 rounded border border-cyan-500/30 bg-cyan-500/12 px-2 py-1 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/20"
              type="button"
            >
              <Download className="h-3 w-3" />
              Export Heatmap Snapshot
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-700/60 bg-zinc-900/82 backdrop-blur-sm px-3 py-2 flex flex-col gap-1.5">
          {/* Layer chips header */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setLayerPanelOpen((v) => !v)}
              className="flex items-center gap-1 text-xs text-zinc-400 uppercase tracking-wider hover:text-zinc-200"
            >
              <Layers className="h-3 w-3" />
              Layers
              {layerPanelOpen ? <ChevronLeft className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
            <span className="text-xs text-zinc-400 font-mono">[L key]</span>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={resetAllLayers}
                title="Reset All Layers"
                className="flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-700 bg-zinc-800/80 text-xs text-zinc-300 hover:bg-zinc-700"
              >
                <RefreshCw className="h-2.5 w-2.5" />
                Reset
              </button>
              <button
                type="button"
                onClick={() => setSavingPreset((v) => !v)}
                title="Save current view as preset"
                className="flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-700/60 bg-indigo-500/15 text-xs text-indigo-200 hover:bg-indigo-500/25"
              >
                <BookmarkPlus className="h-2.5 w-2.5" />
                Save View
              </button>
            </div>
          </div>

          {/* Save preset input */}
          {savingPreset && (
            <div className="flex items-center gap-1">
              <input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveCurrentPreset(); if (e.key === 'Escape') setSavingPreset(false); }}
                placeholder="Preset name (e.g. Election Monitoring)"
                className="flex-1 bg-zinc-950/80 text-zinc-100 placeholder:text-zinc-400 outline-none px-2 py-1 rounded border border-indigo-700/40 text-xs"
              />
              <button type="button" onClick={saveCurrentPreset} className="px-2 py-1 rounded border border-indigo-500/40 bg-indigo-500/20 text-xs text-indigo-100 hover:bg-indigo-500/30">Save</button>
              <button type="button" onClick={() => setSavingPreset(false)} className="px-1.5 py-1 rounded border border-zinc-700 text-xs text-zinc-400 hover:text-zinc-200"><X className="h-3 w-3" /></button>
            </div>
          )}

          {/* Saved preset chips */}
          {savedPresets.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-xs text-zinc-400 uppercase tracking-widest">Presets:</span>
              {savedPresets.map((preset) => (
                <div key={preset.name} className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-indigo-700/40 bg-indigo-500/12 text-xs text-indigo-200 hover:bg-indigo-500/22"
                  >
                    <Bookmark className="h-2.5 w-2.5" />
                    {preset.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => removePreset(preset.name)}
                    className="px-1 py-0.5 rounded text-xs text-zinc-400 hover:text-rose-400"
                  ><X className="h-2.5 w-2.5" /></button>
                </div>
              ))}
            </div>
          )}

          {/* Layer toggle chips */}
          {layerPanelOpen && (
            <div className="flex flex-wrap items-center gap-1.5">
              {LAYER_CONFIG.map((layer) => {
                const active = activeLayers.includes(layer.key);
                const count = layerHotspotCount[layer.key];
                return (
                  <button
                    key={layer.key}
                    type="button"
                    onClick={() => toggleLayer(layer.key)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded text-xs border transition"
                    style={{
                      borderColor: active ? `${layer.color}66` : '#3f3f46',
                      background: active ? `${layer.color}25` : 'rgba(39,39,42,0.8)',
                      color: active ? layer.color : '#a1a1aa',
                    }}
                  >
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ background: active ? layer.color : '#52525b' }}
                    />
                    {layer.label}
                    <span
                      className="ml-0.5 rounded-full px-1 py-0 text-xs font-mono"
                      style={{
                        background: active ? `${layer.color}30` : 'rgba(39,39,42,0.6)',
                        color: active ? layer.color : '#71717a',
                        border: `1px solid ${active ? layer.color + '44' : '#3f3f46'}`,
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-zinc-700/60 bg-zinc-900/80 backdrop-blur-sm px-3 py-2 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMapMode('POINTS')}
              className={`px-2 py-1 text-xs rounded border ${mapMode === 'POINTS' ? 'border-cyan-400/60 bg-cyan-500/20 text-cyan-100' : 'border-zinc-700 text-zinc-400'}`}
            >
              Point Hotspots
            </button>
            <button
              type="button"
              onClick={() => setMapMode('HEAT')}
              className={`px-2 py-1 text-xs rounded border ${mapMode === 'HEAT' ? 'border-cyan-400/60 bg-cyan-500/20 text-cyan-100' : 'border-zinc-700 text-zinc-400'}`}
            >
              Heat Blur
            </button>
          </div>

          {/* Size by toggle */}
          <div className="flex items-center gap-1 border-l border-zinc-700/60 pl-2">
            <span className="text-xs text-zinc-400">Size by:</span>
            <button
              type="button"
              onClick={() => setSizeMode('POSTS')}
              className={`px-2 py-1 text-xs rounded border ${sizeMode === 'POSTS' ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100' : 'border-zinc-700 text-zinc-400'}`}
            >
              Flagged Posts
            </button>
            <button
              type="button"
              onClick={() => setSizeMode('RISK')}
              className={`px-2 py-1 text-xs rounded border ${sizeMode === 'RISK' ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100' : 'border-zinc-700 text-zinc-400'}`}
            >
              Risk Score
            </button>
          </div>

          {/* Cross-platform overlay */}
          <div className="flex items-center gap-1 border-l border-zinc-700/60 pl-2">
            <Monitor className="h-3 w-3 text-violet-300" />
            <button
              type="button"
              onClick={() => setCrossPlatform((v) => !v)}
              className={`px-2 py-1 text-xs rounded border ${crossPlatform ? 'border-violet-400/60 bg-violet-500/20 text-violet-100' : 'border-zinc-700 text-zinc-400'}`}
            >
              X+IG+TikTok+TG
            </button>
          </div>

          {/* Globe toggle */}
          <div className="flex items-center gap-1 border-l border-zinc-700/60 pl-2">
            <Globe2 className="h-3.5 w-3.5 text-indigo-300" />
            <button
              type="button"
              onClick={() => setGlobeMode((value) => !value)}
              className={`px-2 py-1 text-xs rounded border ${globeMode ? 'border-indigo-400/60 bg-indigo-500/20 text-indigo-100' : 'border-zinc-700 text-zinc-400'}`}
            >
              3D Globe [G]
            </button>
            {globeMode && (
              <button
                type="button"
                onClick={() => setGlobePanelOpen((v) => !v)}
                className="px-2 py-1 text-xs rounded border border-indigo-700/50 bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/25"
              >
                Globe Controls
              </button>
            )}
          </div>

          {/* Time window */}
          <div className="flex items-center gap-1 border-l border-zinc-700/60 pl-2">
            {(['LIVE', '1H', '24H', '7D'] as TimeFilter[]).map((windowKey) => (
              <button
                key={windowKey}
                type="button"
                onClick={() => setTimeFilter(windowKey)}
                className={`px-2 py-1 rounded border text-xs ${timeFilter === windowKey ? 'border-amber-400/60 bg-amber-500/20 text-amber-100' : 'border-zinc-700 text-zinc-400'}`}
              >
                {windowKey === 'LIVE' ? 'Live' : windowKey === '1H' ? 'Last 1h' : windowKey === '24H' ? '24h' : '7d'}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2 text-xs text-zinc-300">
            <span>Risk Threshold: {riskThreshold}+</span>
            <input
              type="range"
              min={35}
              max={90}
              value={riskThreshold}
              onChange={(event) => setRiskThreshold(Number(event.target.value))}
              className="accent-rose-500"
            />
          </div>
        </div>

        <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 backdrop-blur-sm px-3 py-1.5 text-xs text-cyan-100 flex items-center gap-2">
          <BrainCircuit className="h-3.5 w-3.5 text-cyan-200" />
          AI Insight: {aiInsights[insightIndex % aiInsights.length]}
        </div>
      </div>

      <div className="absolute top-3 right-3 z-[460] text-xs font-medium text-zinc-200 rounded border border-zinc-700/60 bg-zinc-900/85 backdrop-blur-sm px-2 py-1 flex items-center gap-2">
        <span className={`inline-flex h-2 w-2 rounded-full ${loading ? 'bg-amber-400 animate-pulse' : isLive ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-500'}`} />
        {loading ? 'Loading feed...' : isLive ? 'Live OSINT stream' : 'Demo data stream'}
      </div>

      <div className="osint-scan absolute left-0 right-0 h-px z-[350] pointer-events-none"
        style={{ background: 'linear-gradient(to right, transparent, #06b6d460, #06b6d4cc, #06b6d460, transparent)' }} />

      <MapContainer
        center={[20, 20]}
        zoom={2}
        style={{ height: '100%', width: '100%', background: '#09090b' }}
        zoomControl={false}
        scrollWheelZoom
        attributionControl={false}
      >
        <TileLayer
          url={
            globeMode
              ? 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png'
              : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
          }
        />

        {/* Auto-rotate child controller */}
        <AutoRotateController enabled={autoRotate} speed={rotateSpeed} />

        {visibleHotspots.map((loc) => {
          const riskScore = (loc.avg_risk ?? loc.confidence) * 100;
          const radius = sizeMode === 'RISK'
            ? clamp(riskScore * 700, 18000, 120000)
            : clamp(loc.posts * 1.1, 18000, 120000);
          return (
            <Circle
              key={`blur-${loc.id}`}
              center={[loc.lat, loc.lng]}
              radius={radius}
              pathOptions={{
                color: colorForRisk(riskScore),
                fillColor: colorForRisk(riskScore),
                fillOpacity: mapMode === 'HEAT' ? 0.24 : 0.08,
                weight: mapMode === 'HEAT' ? 1 : 0.4,
                opacity: mapMode === 'HEAT' ? 0.6 : 0.2,
              }}
            >
              <Tooltip direction="top" opacity={0.95}>
                <div className="text-xs text-white font-mono">
                  {loc.name} | Risk {Math.round(riskScore)} | Posts {loc.posts.toLocaleString()}
                </div>
              </Tooltip>
            </Circle>
          );
        })}

        {propagationLines.map((line, idx) => (
          <Polyline
            key={`line-${idx}`}
            positions={[line.from, line.to]}
            pathOptions={{ color: line.color, weight: line.weight, opacity: 0.45, dashArray: '6 8' }}
          />
        ))}

        {/* Flight arc between selected cities */}
        {flightArc && (
          <Polyline
            key={`flight-${flightArc.fromName}-${flightArc.toName}`}
            positions={[flightArc.from, flightArc.to]}
            pathOptions={{ color: '#22d3ee', weight: 3, opacity: 0.9, dashArray: '8 6' }}
          />
        )}

        {markers.map((loc) => (
          <Marker
            key={loc.id}
            position={[loc.lat, loc.lng]}
            icon={loc.icon}
            eventHandlers={{
              click: () => {
                handleFlightClick(loc.id);
                setSelectedId(loc.id);
                onAddCompare(loc.id);
              },
            }}
          >
            <Tooltip direction="top" opacity={1}>
              <div style={{
                background: '#18181b',
                border: `1px solid ${SEV[loc.severity].color}50`,
                borderRadius: 8, padding: '8px 12px',
                fontFamily: 'monospace', fontSize: 11, color: '#fff', minWidth: 170,
                boxShadow: `0 4px 20px ${SEV[loc.severity].color}30`,
              }}>
                <div style={{ color: SEV[loc.severity].color, fontWeight: 700, marginBottom: 4 }}>
                  {loc.severity} — {loc.name}
                  {flightMode && <span style={{ color: '#22d3ee', fontSize: 9, marginLeft: 6 }}>✈ Click to route</span>}
                </div>
                <div><span style={{ color: '#71717a' }}>Region: </span>{loc.region}</div>
                <div><span style={{ color: '#71717a' }}>Flagged Posts: </span><b>{loc.posts.toLocaleString()}</b></div>
                <div><span style={{ color: '#71717a' }}>Avg Risk Score: </span>
                  <b style={{ color: SEV[loc.severity].color }}>
                    {loc.avg_risk != null ? (loc.avg_risk * 100).toFixed(0) + '%' : (loc.confidence * 100).toFixed(0) + '%'}
                  </b>
                </div>
                <div><span style={{ color: '#71717a' }}>Confidence: </span>{(loc.confidence * 100).toFixed(0)}%</div>
                {isLive && (
                  <div style={{ color: '#22c55e', fontSize: 9, marginTop: 4 }}>● LIVE DATA · 6-Layer OSINT</div>
                )}
              </div>
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>

      {/* ── Globe Controls Floating Panel ───────────────────────── */}
      {globeMode && (
        <div className={`absolute right-0 top-[30%] z-[480] flex transition-all duration-300 ${globePanelOpen ? 'translate-x-0' : 'translate-x-[calc(100%-24px)]'}`}>
          {/* Pull-tab */}
          <button
            type="button"
            onClick={() => setGlobePanelOpen((v) => !v)}
            className="flex h-24 w-6 flex-col items-center justify-center rounded-l-lg border border-indigo-700/60 border-r-0 bg-indigo-900/80 backdrop-blur-sm text-indigo-200 hover:bg-indigo-800/90"
            title="Globe Controls"
          >
            <RotateCcw className="h-3 w-3 mb-1" />
            {globePanelOpen ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
          </button>

          {/* Panel body */}
          <div className="w-[200px] rounded-l-none rounded-r-none border border-l-0 border-indigo-700/50 bg-zinc-900/94 backdrop-blur-md p-3 flex flex-col gap-3 shadow-2xl">
            <div className="text-xs font-semibold text-indigo-200 uppercase tracking-widest flex items-center gap-1">
              <Globe2 className="h-3 w-3" />
              Globe Controls
            </div>

            {/* Auto-rotate */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-300 flex items-center gap-1"><RotateCcw className="h-3 w-3 text-indigo-300" /> Auto-Rotate</span>
                <button
                  type="button"
                  onClick={() => setAutoRotate((v) => !v)}
                  className={`px-2 py-0.5 rounded text-xs border ${autoRotate ? 'border-indigo-400/60 bg-indigo-500/30 text-indigo-100' : 'border-zinc-700 text-zinc-400'}`}
                >
                  {autoRotate ? 'ON' : 'OFF'}
                </button>
              </div>
              {autoRotate && (
                <div className="flex items-center gap-2 text-xs text-zinc-300">
                  <span>Speed: {rotateSpeed}x</span>
                  <input
                    type="range" min={1} max={5} value={rotateSpeed}
                    onChange={(e) => setRotateSpeed(Number(e.target.value))}
                    className="flex-1 accent-indigo-500"
                  />
                </div>
              )}
            </div>

            {/* Tilt presets */}
            <div className="space-y-1">
              <div className="text-xs text-zinc-400 uppercase tracking-wider">Perspective</div>
              {([['BIRDS_EYE', "Bird's Eye", '42°'], ['EQUATOR', 'Equator View', '21°'], ['POLAR', 'Polar View', '7°']] as [GlobeTilt, string, string][]).map(([key, label, angle]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setGlobeTilt(key)}
                  className={`w-full text-left px-2 py-1 rounded border text-xs flex items-center justify-between ${globeTilt === key ? 'border-indigo-400/60 bg-indigo-500/20 text-indigo-100' : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}
                >
                  {label}
                  <span className="text-xs opacity-60">{angle}</span>
                </button>
              ))}
            </div>

            {/* Flight mode */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-300 flex items-center gap-1"><Navigation className="h-3 w-3 text-cyan-300" /> Flight Mode</span>
                <button
                  type="button"
                  onClick={() => { setFlightMode((v) => !v); setFlightCities([]); }}
                  className={`px-2 py-0.5 rounded text-xs border ${flightMode ? 'border-cyan-400/60 bg-cyan-500/25 text-cyan-100' : 'border-zinc-700 text-zinc-400'}`}
                >
                  {flightMode ? 'ON' : 'OFF'}
                </button>
              </div>
              {flightMode && (
                <div className="text-xs text-zinc-400 bg-zinc-800/70 rounded px-2 py-1">
                  {flightCities.length === 0 && 'Click 2 hotspots on map'}
                  {flightCities.length === 1 && `From: ${timelineHotspots.find(s => s.id === flightCities[0])?.name ?? '?'} — click destination`}
                  {flightCities.length === 2 && flightArc && (
                    <span className="text-cyan-300">{flightArc.fromName} → {flightArc.toName}</span>
                  )}
                  {flightCities.length >= 2 && (
                    <button
                      type="button"
                      onClick={() => setFlightCities([])}
                      className="ml-1 text-rose-300 hover:text-rose-200"
                    >Clear</button>
                  )}
                </div>
              )}
            </div>

            {/* Keyboard reference */}
            <div className="border-t border-zinc-800/60 pt-2 text-xs text-zinc-400 space-y-0.5">
              <div><kbd className="bg-zinc-800 px-1 rounded text-zinc-300">G</kbd> Toggle 3D/2D</div>
              <div><kbd className="bg-zinc-800 px-1 rounded text-zinc-300">L</kbd> Toggle layer panel</div>
              <div><kbd className="bg-zinc-800 px-1 rounded text-zinc-300">Esc</kbd> Clear flight</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Cross-platform overlay badge ─────────────────────────── */}
      {crossPlatform && (
        <div className="absolute left-3 top-1/2 z-[470] -translate-y-1/2 pointer-events-none">
          <div className="rounded-lg border border-violet-500/40 bg-violet-900/80 backdrop-blur-sm px-2.5 py-2 text-xs text-violet-100 shadow-lg space-y-1">
            <div className="font-semibold text-violet-200 flex items-center gap-1"><Monitor className="h-3 w-3" /> Cross-Platform Overlay</div>
            {[['X (Twitter)', '#06b6d4'], ['Instagram', '#a78bfa'], ['TikTok', '#ec4899'], ['Telegram', '#22c55e'], ['Dark Web', '#f43f5e']].map(([label, color]) => (
              <div key={label} className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
                <span style={{ color }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedHotspot && (
        <>
          {/* Backdrop dim — click to close */}
          <div
            className="absolute inset-0 z-[465] bg-zinc-950/50 backdrop-blur-[1.5px]"
            onClick={() => setSelectedId('')}
          />

          {/* Full-height intelligence drawer */}
          <div className="absolute right-0 top-0 bottom-0 z-[470] w-[460px] max-w-[92vw] flex flex-col border-l border-zinc-700/70 bg-zinc-950/98 backdrop-blur-xl shadow-[−20px_0_60px_rgba(0,0,0,0.7)]">

            {/* ── HEADER ── */}
            <div className="px-4 pt-4 pb-3 border-b border-zinc-800/80 bg-zinc-900/60 flex-shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <div
                    className="mt-0.5 w-3 h-3 rounded-full flex-shrink-0"
                    style={{ background: SEV[selectedHotspot.severity].color, boxShadow: `0 0 0 2px ${SEV[selectedHotspot.severity].color}50` }}
                  />
                  <div className="min-w-0">
                    <div className="text-zinc-100 text-sm font-bold tracking-wide leading-tight truncate">
                      {selectedHotspot.name}
                    </div>
                    <div className="text-zinc-400 text-xs mt-0.5">{selectedHotspot.region} · Intelligence Analysis</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div
                    className="text-[11px] px-2.5 py-1 rounded-full border font-semibold"
                    style={{
                      color: SEV[selectedHotspot.severity].color,
                      borderColor: `${SEV[selectedHotspot.severity].color}70`,
                      background: `${SEV[selectedHotspot.severity].color}18`,
                    }}
                  >
                    {selectedHotspot.severity} {selectedHotspot.escalating ? '↑ Escalating' : '· Stable'}
                  </div>
                  <button
                    type="button"
                    className="text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 p-1 rounded transition-colors"
                    onClick={() => setSelectedId('')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* ── METRICS STRIP ── */}
            <div className="grid grid-cols-3 border-b border-zinc-800/70 flex-shrink-0 bg-zinc-900/30">
              <div className="px-4 py-3 border-r border-zinc-800/70 text-center">
                <div className="text-xl font-bold text-rose-300 tabular-nums">
                  {selectedHotspot.posts.toLocaleString()}
                </div>
                <div className="text-xs text-zinc-400 mt-0.5 uppercase tracking-wider">Flagged Posts</div>
              </div>
              <div className="px-4 py-3 border-r border-zinc-800/70 text-center">
                <div className="text-xl font-bold tabular-nums" style={{ color: SEV[selectedHotspot.severity].color }}>
                  {Math.round((selectedHotspot.avg_risk ?? selectedHotspot.confidence) * 100)}
                  <span className="text-xs text-zinc-400 font-normal">/100</span>
                </div>
                <div className="text-xs text-zinc-400 mt-0.5 uppercase tracking-wider">Risk Score</div>
              </div>
              <div className="px-4 py-3 text-center">
                <div className="text-xl font-bold text-cyan-300 tabular-nums">
                  {Math.round(selectedHotspot.confidence * 100)}%
                </div>
                <div className="text-xs text-zinc-400 mt-0.5 uppercase tracking-wider">Confidence</div>
              </div>
            </div>

            {/* ── RISK BAR ── */}
            <div className="px-4 py-2 border-b border-zinc-800/50 flex-shrink-0 bg-zinc-900/20">
              <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                <span>Risk Level</span>
                <span style={{ color: SEV[selectedHotspot.severity].color }}>
                  {Math.round((selectedHotspot.avg_risk ?? selectedHotspot.confidence) * 100)}/100
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-zinc-800">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round((selectedHotspot.avg_risk ?? selectedHotspot.confidence) * 100)}%`,
                    background: `linear-gradient(90deg, ${SEV[selectedHotspot.severity].color}88, ${SEV[selectedHotspot.severity].color})`,
                  }}
                />
              </div>
            </div>

            {/* ── TABS ── */}
            <div className="px-3 py-2.5 border-b border-zinc-800/70 flex gap-1.5 flex-wrap flex-shrink-0">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1.5 text-[11px] rounded-md border transition-colors ${
                    activeTab === tab
                      ? 'border-cyan-400/60 bg-cyan-500/20 text-cyan-100 font-medium'
                      : 'border-zinc-700/70 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600'
                  }`}
                >
                  {tabLabel(tab)}
                </button>
              ))}
            </div>

            {/* ── TAB CONTENT (scrollable) ── */}
            <div className="flex-1 overflow-y-auto">

              {activeTab === 'OVERVIEW' && (
                <div className="px-4 py-4 space-y-4">
                  {/* Primary Actors */}
                  <div>
                    <div className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Primary Actors</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedHotspot.actors.map((actor) => (
                        <span
                          key={actor}
                          className="px-2.5 py-1 rounded-full text-[11px] border border-violet-500/30 bg-violet-500/15 text-violet-200"
                        >
                          {actor}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Status flags */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2.5">
                      <div className="text-xs text-zinc-400 mb-1">Escalation Status</div>
                      <div className={`text-sm font-semibold ${selectedHotspot.escalating ? 'text-rose-400' : 'text-emerald-400'}`}>
                        {selectedHotspot.escalating ? '↑ Escalating' : '✓ Stable'}
                      </div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2.5">
                      <div className="text-xs text-zinc-400 mb-1">Layer Classification</div>
                      <div className="text-sm font-semibold text-indigo-300 capitalize">
                        {selectedHotspot.layers[0]?.replace('_', ' ') ?? '—'}
                      </div>
                    </div>
                  </div>

                  {/* Region detail */}
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-3 space-y-2">
                    <div className="text-xs text-zinc-400 uppercase tracking-wider">Region Detail</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      <div className="text-zinc-400">Region</div>
                      <div className="text-zinc-200">{selectedHotspot.region}</div>
                      <div className="text-zinc-400">Coordinates</div>
                      <div className="text-zinc-300 font-mono text-[11px]">
                        {selectedHotspot.lat.toFixed(2)}°, {selectedHotspot.lng.toFixed(2)}°
                      </div>
                      <div className="text-zinc-400">Severity Tier</div>
                      <div className="font-semibold" style={{ color: SEV[selectedHotspot.severity].color }}>
                        {selectedHotspot.severity}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'NARRATIVES' && (
                <div className="px-4 py-4 space-y-2.5">
                  <div className="text-xs text-zinc-400 uppercase tracking-wider mb-3">
                    Active Narrative Clusters · {selectedHotspot.narrativeClusters.length} detected
                  </div>
                  {selectedHotspot.narrativeClusters.map((item, i) => (
                    <div
                      key={item}
                      className="rounded-lg border border-zinc-800/80 bg-zinc-900/50 px-3 py-3 hover:border-zinc-700 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: SEV[selectedHotspot.severity].color }}
                        />
                        <div>
                          <div className="text-xs text-zinc-200">{item}</div>
                          <div className="text-xs text-zinc-400 mt-1">Cluster #{i + 1} · Spreading across {selectedHotspot.region}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'LINKED' && (
                <div className="px-4 py-4 space-y-2.5">
                  <div className="text-xs text-zinc-400 uppercase tracking-wider mb-3">
                    Linked Intelligence Signals · {selectedHotspot.linkedSignals.length} signals
                  </div>
                  {selectedHotspot.linkedSignals.map((item, i) => (
                    <div
                      key={item}
                      className="rounded-lg border border-cyan-900/40 bg-cyan-950/20 px-3 py-3 hover:border-cyan-700/40 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 w-4 h-4 rounded-sm bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-cyan-400">{i + 1}</span>
                        </div>
                        <div className="text-xs text-zinc-300">{item}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'VELOCITY' && (
                <div className="px-4 py-4 space-y-3">
                  <div className="text-xs text-zinc-400 uppercase tracking-wider">Risk Velocity Trend</div>
                  <div className="h-52 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={selectedHotspot.velocityTrend} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis
                          dataKey="t"
                          tick={{ fill: '#71717a', fontSize: 10 }}
                          axisLine={{ stroke: '#3f3f46' }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: '#71717a', fontSize: 10 }}
                          axisLine={{ stroke: '#3f3f46' }}
                          tickLine={false}
                          width={28}
                        />
                        <RechartsTooltip
                          contentStyle={{ backgroundColor: '#09090b', borderColor: '#27272a', borderRadius: 8, fontSize: 11 }}
                          labelStyle={{ color: '#d4d4d8' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="risk"
                          stroke="#22d3ee"
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: '#22d3ee', strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: '#22d3ee' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
                    <div className="text-xs text-zinc-400 mb-1.5">Trend Summary</div>
                    <div className="text-xs text-zinc-300">
                      {selectedHotspot.escalating
                        ? 'Risk score is rising — threat is actively escalating. Recommend immediate monitoring.'
                        : 'Risk score is holding steady. Situation is being contained. Monitor for resurgence.'}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'IMPACT' && (
                <div className="px-4 py-4 space-y-3">
                  <div className="text-xs text-zinc-400 uppercase tracking-wider">Projected Impact Assessment</div>
                  <div
                    className="rounded-xl border px-4 py-4 text-sm leading-relaxed"
                    style={{
                      borderColor: `${SEV[selectedHotspot.severity].color}40`,
                      background: `${SEV[selectedHotspot.severity].color}0d`,
                      color: '#e4e4e7',
                    }}
                  >
                    {selectedHotspot.projectedImpact}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-amber-900/40 bg-amber-950/20 px-3 py-2.5 text-center">
                      <div className="text-base font-bold text-amber-300">
                        {selectedHotspot.escalating ? 'HIGH' : 'MED'}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">Spread Risk</div>
                    </div>
                    <div className="rounded-lg border border-rose-900/40 bg-rose-950/20 px-3 py-2.5 text-center">
                      <div className="text-base font-bold text-rose-300">
                        {selectedHotspot.posts > 5000 ? 'CRITICAL' : 'MODERATE'}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">Volume Impact</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── ACTION BUTTONS ── */}
            <div className="px-3 py-3 border-t border-zinc-800/80 grid grid-cols-2 gap-2 flex-shrink-0 bg-zinc-900/40">
              <button
                type="button"
                className="text-[11px] rounded-lg border border-cyan-500/40 bg-cyan-500/15 text-cyan-100 px-3 py-2.5 hover:bg-cyan-500/25 transition-colors font-medium"
              >
                Drill into Narrative Mapping
              </button>
              <button
                type="button"
                className="text-[11px] rounded-lg border border-emerald-500/40 bg-emerald-500/15 text-emerald-100 px-3 py-2.5 hover:bg-emerald-500/25 transition-colors font-medium"
              >
                Add to Crisis Workspace
              </button>
              <button
                type="button"
                className="text-[11px] rounded-lg border border-amber-500/40 bg-amber-500/15 text-amber-100 px-3 py-2.5 hover:bg-amber-500/25 transition-colors font-medium"
              >
                Generate Takedown Package
              </button>
              <button
                type="button"
                className="text-[11px] rounded-lg border border-indigo-500/40 bg-indigo-500/15 text-indigo-100 px-3 py-2.5 hover:bg-indigo-500/25 transition-colors font-medium"
              >
                Export OSINT Report
              </button>
            </div>
          </div>
        </>
      )}

      {compareCities.length > 0 && (
        <div className="absolute left-3 right-3 bottom-14 z-[460] rounded-lg border border-zinc-700/60 bg-zinc-900/85 backdrop-blur-sm p-2">
          <div className="flex items-center gap-1 text-xs text-zinc-300 mb-1.5">
            <Activity className="h-3.5 w-3.5 text-cyan-300" />
            Click-and-drag multi-city comparison (max 4)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {compareCities.map((id, index) => {
              const spot = timelineHotspots.find((item) => item.id === id);
              if (!spot) return null;
              const risk = Math.round((spot.avg_risk ?? spot.confidence) * 100);
              return (
                <button
                  key={id}
                  type="button"
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => onDropCompare(index)}
                  onDoubleClick={() => onRemoveCompare(id)}
                  className="rounded border px-2 py-1 text-xs text-left"
                  style={{
                    borderColor: `${colorForRisk(risk)}80`,
                    background: `${colorForRisk(risk)}22`,
                    color: '#e4e4e7',
                  }}
                  title="Drag to reorder. Double click to remove."
                >
                  {spot.name} | {risk}/100 | {spot.posts.toLocaleString()} posts
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="absolute left-3 right-3 bottom-3 z-[460] rounded-lg border border-zinc-700/60 bg-zinc-900/88 backdrop-blur-sm px-3 py-2">
        <div className="flex items-center justify-between gap-3 text-xs text-zinc-300">
          <div className="flex items-center gap-3">
            <span className="text-zinc-100 font-semibold">Legend</span>
            <span className="text-zinc-400">Size = Flagged Posts volume</span>
            <span className="text-zinc-400">Color = Avg Risk Score</span>
            <span className="text-zinc-400">Border = Confidence level</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-400">
            <Clock3 className="h-3.5 w-3.5" />
            Last Updated: {toDisplayTime(lastUpdated)} (live refresh every 30s)
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs">
          <span className="text-rose-300 font-semibold">{totalThreats.toLocaleString()}</span>
          <span className="text-zinc-400">flagged posts</span>
          <span className="text-amber-300 font-semibold">{visibleHotspots.length}</span>
          <span className="text-zinc-400">hotspots in current filter</span>
          <span className="ml-auto text-cyan-200 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Dynamic Multi-Layer Threat Globe</span>
        </div>
      </div>

      {/* ── GLASS HEATMAP EXPANSION OVERLAY ── */}
    {heatExpanded && (
      <div
        className="fixed inset-0 z-[1500] flex"
        onMouseLeave={(e) => {
          // Only close when truly leaving the overlay (not child elements)
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setHeatExpanded(false);
        }}
      >
        {/* Semi-transparent glass backdrop — original blinking dots visible behind */}
        <div className="absolute inset-0 bg-zinc-950/78 backdrop-blur-[10px]" />

        {/* Modal content */}
        <div className="relative z-10 flex w-full h-full p-3 gap-3" onClick={(e) => e.stopPropagation()}>

          {/* ── LEFT: Full heatmap map ── */}
          <div className="flex-1 relative rounded-2xl overflow-hidden border border-cyan-500/20 shadow-[0_0_80px_rgba(6,182,212,0.18)]">
            {/* Animated scan line */}
            <div
              className="osint-scan absolute left-0 right-0 h-px z-[350] pointer-events-none"
              style={{ background: 'linear-gradient(to right, transparent, #06b6d460, #06b6d4cc, #06b6d460, transparent)' }}
            />

            <MapContainer
              center={[20, 10]}
              zoom={2}
              style={{ height: '100%', width: '100%', background: '#09090b' }}
              zoomControl={false}
              scrollWheelZoom
              attributionControl={false}
            >
              <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png" />

              {/* Heat circles */}
              {visibleHotspots.map((loc) => {
                const riskScore = (loc.avg_risk ?? loc.confidence) * 100;
                const radius = clamp(riskScore * 700, 22000, 130000);
                return (
                  <Circle
                    key={`glass-circle-${loc.id}`}
                    center={[loc.lat, loc.lng]}
                    radius={radius}
                    pathOptions={{
                      color: colorForRisk(riskScore),
                      fillColor: colorForRisk(riskScore),
                      fillOpacity: 0.30,
                      weight: 1.5,
                      opacity: 0.75,
                    }}
                  >
                    <Tooltip direction="top" opacity={0.95}>
                      <span className="text-xs font-mono">
                        {loc.name} · Risk {Math.round(riskScore)} · {loc.posts.toLocaleString()} posts
                      </span>
                    </Tooltip>
                  </Circle>
                );
              })}

              {/* Propagation lines */}
              {propagationLines.map((line, idx) => (
                <Polyline
                  key={`glass-line-${idx}`}
                  positions={[line.from, line.to]}
                  pathOptions={{ color: line.color, weight: line.weight + 1, opacity: 0.55, dashArray: '6 8' }}
                />
              ))}

              {/* Blinking point markers */}
              {markers.map((loc) => (
                <Marker
                  key={`glass-marker-${loc.id}`}
                  position={[loc.lat, loc.lng]}
                  icon={loc.icon}
                  eventHandlers={{ click: () => { setSelectedId(loc.id); setHeatExpanded(false); } }}
                >
                  <Tooltip direction="top" opacity={0.95}>
                    <span className="text-xs font-mono">{loc.name} · {Math.round((loc.avg_risk ?? loc.confidence) * 100)} risk</span>
                  </Tooltip>
                </Marker>
              ))}
            </MapContainer>

            {/* Edge vignette */}
            <div
              className="absolute inset-0 pointer-events-none z-[400]"
              style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(9,9,11,0.55) 100%)' }}
            />

            {/* Map label */}
            <div className="absolute top-3 left-3 z-[450] flex items-center gap-2 text-[11px] text-cyan-200 bg-zinc-950/70 backdrop-blur px-3 py-1.5 rounded-lg border border-cyan-500/20">
              <Globe2 className="h-3.5 w-3.5 text-cyan-300" />
              Global Threat Heatmap · {visibleHotspots.length} Active Hotspots
            </div>

            {/* Close button on map */}
            <button
              type="button"
              onClick={() => setHeatExpanded(false)}
              className="absolute top-3 right-3 z-[450] p-1.5 rounded-lg border border-zinc-700/60 bg-zinc-900/80 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* ── RIGHT: Analysis panel ── */}
          <div className="w-[320px] flex-shrink-0 flex flex-col gap-3 overflow-y-auto">

            {/* Header */}
            <div className="flex items-center justify-between flex-shrink-0">
              <div>
                <div className="text-sm font-bold text-zinc-100 tracking-wide">Heatmap Analysis</div>
                <div className="text-xs text-zinc-400 mt-0.5">Live OSINT · {toDisplayTime(lastUpdated)}</div>
              </div>
              <button
                type="button"
                onClick={() => setHeatExpanded(false)}
                className="p-1.5 rounded-lg border border-zinc-700 bg-zinc-900/80 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Global stats grid */}
            <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 backdrop-blur-sm p-4 grid grid-cols-2 gap-3 flex-shrink-0">
              <div className="text-center">
                <div className="text-2xl font-bold tabular-nums" style={{ color: colorForRisk(globalRiskScore) }}>
                  {globalRiskScore}
                </div>
                <div className="text-xs text-zinc-400 mt-0.5">Global Risk</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-zinc-100 tabular-nums">{visibleHotspots.length}</div>
                <div className="text-xs text-zinc-400 mt-0.5">Active Nodes</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-rose-300 tabular-nums">{totalThreats.toLocaleString()}</div>
                <div className="text-xs text-zinc-400 mt-0.5">Flagged Posts</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-amber-300 tabular-nums">
                  {visibleHotspots.filter((s) => s.severity === 'CRITICAL').length}
                </div>
                <div className="text-xs text-zinc-400 mt-0.5">Critical Zones</div>
              </div>
            </div>

            {/* Layer breakdown */}
            <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 backdrop-blur-sm p-4 flex-shrink-0">
              <div className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Layer Breakdown</div>
              {LAYER_CONFIG.map((layer) => {
                const count = layerHotspotCount[layer.key as LayerKey] ?? 0;
                const maxCount = Math.max(...LAYER_CONFIG.map((l) => layerHotspotCount[l.key as LayerKey] ?? 0), 1);
                return (
                  <div key={layer.key} className="mb-2.5 last:mb-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-zinc-300">{layer.label}</span>
                      <span className="text-xs font-mono tabular-nums" style={{ color: layer.color }}>{count}</span>
                    </div>
                    <div className="h-1 rounded-full bg-zinc-800 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${(count / maxCount) * 100}%`, background: layer.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Escalation alert */}
            {escalatingCount > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-950/30 px-4 py-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5 text-amber-300 flex-shrink-0" />
                  <div className="text-[11px] text-amber-200 font-medium">
                    {escalatingCount} hotspot{escalatingCount > 1 ? 's' : ''} escalating right now
                  </div>
                </div>
              </div>
            )}

            {/* Hotspot rankings */}
            <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 backdrop-blur-sm p-4 flex-1 min-h-0">
              <div className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Hotspot Rankings · by Risk</div>
              <div className="space-y-2 overflow-y-auto">
                {[...visibleHotspots]
                  .sort((a, b) => (b.avg_risk ?? b.confidence) - (a.avg_risk ?? a.confidence))
                  .map((spot, i) => {
                    const riskScore = Math.round((spot.avg_risk ?? spot.confidence) * 100);
                    return (
                      <button
                        key={spot.id}
                        type="button"
                        onClick={() => { setSelectedId(spot.id); setHeatExpanded(false); }}
                        className="w-full rounded-lg border border-zinc-800/80 bg-zinc-900/50 px-3 py-2.5 text-left hover:border-zinc-600 hover:bg-zinc-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400 font-mono w-4">#{i + 1}</span>
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: SEV[spot.severity].color }} />
                          <span className="text-[11px] text-zinc-200 flex-1 truncate">{spot.name}</span>
                          <span className="text-xs font-mono tabular-nums" style={{ color: colorForRisk(riskScore) }}>
                            {riskScore}
                          </span>
                        </div>
                        <div className="mt-1.5 h-0.5 rounded-full bg-zinc-800 overflow-hidden ml-5">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${riskScore}%`, background: colorForRisk(riskScore), opacity: 0.8 }}
                          />
                        </div>
                        <div className="mt-1 ml-5 text-xs text-zinc-400 flex items-center gap-1.5">
                          <span>{spot.region}</span>
                          <span>·</span>
                          <span>{spot.posts.toLocaleString()} posts</span>
                          <span>·</span>
                          <span>{spot.severity}</span>
                          {spot.escalating && <span className="text-amber-400">↑ Escalating</span>}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

      {/* ── NODE PROPAGATION TREE EXPANSION OVERLAY ── */}
    {propExpanded && (
      <div
        className="fixed inset-0 z-[1500] flex"
        onMouseLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setPropExpanded(false);
        }}
      >
        <div className="absolute inset-0 bg-zinc-950/78 backdrop-blur-[10px]" />

        <div className="relative z-10 flex w-full h-full p-3 gap-3" onClick={(e) => e.stopPropagation()}>

          {/* ── LEFT: SVG node propagation graph ── */}
          <div className="flex-1 relative rounded-2xl overflow-hidden border border-violet-500/20 shadow-[0_0_80px_rgba(139,92,246,0.18)]">
            {/* Animated scan line */}
            <div
              className="osint-scan absolute left-0 right-0 h-px z-[350] pointer-events-none"
              style={{ background: 'linear-gradient(to right, transparent, #7c3aed60, #8b5cf6cc, #7c3aed60, transparent)' }}
            />

            <svg
              viewBox="0 0 1000 580"
              className="w-full h-full"
              style={{ background: '#09090b' }}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <marker id="pt-arrow-rose" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                  <path d="M0,0 L7,3.5 L0,7 Z" fill="#f43f5e" opacity="0.85" />
                </marker>
                <marker id="pt-arrow-amber" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                  <path d="M0,0 L7,3.5 L0,7 Z" fill="#f59e0b" opacity="0.85" />
                </marker>
                <marker id="pt-arrow-cyan" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                  <path d="M0,0 L7,3.5 L0,7 Z" fill="#22d3ee" opacity="0.85" />
                </marker>
                <marker id="pt-arrow-blue" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                  <path d="M0,0 L7,3.5 L0,7 Z" fill="#3b82f6" opacity="0.85" />
                </marker>
              </defs>

              {/* Subtle world-grid lines */}
              {([-30, 0, 30, 60] as number[]).map((lat) => {
                const gy = 20 + ((90 - lat) / 180) * 540;
                return <line key={`g-lat-${lat}`} x1="30" x2="970" y1={gy} y2={gy} stroke="#27272a" strokeWidth="0.5" />;
              })}
              {([-120, -60, 0, 60, 120] as number[]).map((lng) => {
                const gx = 30 + ((lng + 180) / 360) * 940;
                return <line key={`g-lng-${lng}`} x1={gx} x2={gx} y1="20" y2="560" stroke="#27272a" strokeWidth="0.5" />;
              })}
              {/* Grid axis labels */}
              {([-30, 0, 30, 60] as number[]).map((lat) => {
                const gy = 20 + ((90 - lat) / 180) * 540;
                return (
                  <text key={`l-lat-${lat}`} x="26" y={gy + 3} textAnchor="end" fill="#52525b" fontSize="7" fontFamily="monospace">
                    {lat}°
                  </text>
                );
              })}

              {/* Propagation edges (curved arcs with arrowheads) */}
              {propagationLines.map((line, idx) => {
                const x1 = 30 + ((line.from[1] + 180) / 360) * 940;
                const y1 = 20 + ((90 - line.from[0]) / 180) * 540;
                const x2 = 30 + ((line.to[1] + 180) / 360) * 940;
                const y2 = 20 + ((90 - line.to[0]) / 180) * 540;
                const cx = (x1 + x2) / 2;
                const cy = (y1 + y2) / 2 - Math.max(40, Math.abs(x2 - x1) * 0.22);
                const markerId =
                  line.color === '#f43f5e' ? 'pt-arrow-rose'
                  : line.color === '#f59e0b' ? 'pt-arrow-amber'
                  : line.color === '#22d3ee' ? 'pt-arrow-cyan'
                  : 'pt-arrow-blue';
                return (
                  <g key={`edge-${idx}`}>
                    {/* Soft glow behind edge */}
                    <path
                      d={`M${x1},${y1} Q${cx},${cy} ${x2},${y2}`}
                      fill="none"
                      stroke={line.color}
                      strokeWidth={line.weight + 4}
                      opacity="0.10"
                    />
                    {/* Main dashed edge */}
                    <path
                      d={`M${x1},${y1} Q${cx},${cy} ${x2},${y2}`}
                      fill="none"
                      stroke={line.color}
                      strokeWidth={line.weight + 0.5}
                      strokeDasharray="10 6"
                      opacity="0.72"
                      markerEnd={`url(#${markerId})`}
                    />
                  </g>
                );
              })}

              {/* Hotspot nodes */}
              {visibleHotspots.map((spot) => {
                const x = 30 + ((spot.lng + 180) / 360) * 940;
                const y = 20 + ((90 - spot.lat) / 180) * 540;
                const riskScore = Math.round((spot.avg_risk ?? spot.confidence) * 100);
                const r = 8 + (riskScore / 100) * 10;
                const color = SEV[spot.severity].color;
                return (
                  <g key={spot.id} style={{ cursor: 'pointer' }} onClick={() => { setSelectedId(spot.id); setPropExpanded(false); }}>
                    {/* Outer pulse ring */}
                    <circle cx={x} cy={y} r={r + 5} fill="none" stroke={color} strokeWidth="1" opacity="0.20" />
                    {/* Node fill */}
                    <circle cx={x} cy={y} r={r} fill={`${color}22`} stroke={color} strokeWidth="1.5" opacity="0.92" />
                    {/* Risk score label inside */}
                    <text x={x} y={y + 3} textAnchor="middle" dominantBaseline="middle" fill={color} fontSize="8" fontWeight="700" fontFamily="monospace">
                      {riskScore}
                    </text>
                    {/* City name below node */}
                    <text x={x} y={y + r + 13} textAnchor="middle" fill="#d4d4d8" fontSize="9" fontFamily="sans-serif">
                      {spot.name}
                    </text>
                    {/* Escalating indicator */}
                    {spot.escalating && (
                      <text x={x + r + 2} y={y - r + 2} fill="#fbbf24" fontSize="10" fontWeight="bold">↑</text>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Edge vignette */}
            <div
              className="absolute inset-0 pointer-events-none z-[400]"
              style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(9,9,11,0.55) 100%)' }}
            />

            {/* Map label */}
            <div className="absolute top-3 left-3 z-[450] flex items-center gap-2 text-[11px] text-violet-200 bg-zinc-950/70 backdrop-blur px-3 py-1.5 rounded-lg border border-violet-500/20">
              <GitBranch className="h-3.5 w-3.5 text-violet-300" />
              Node Propagation Tree · {visibleHotspots.length} nodes · {propagationLines.length} edges
            </div>

            {/* Close button on graph */}
            <button
              type="button"
              onClick={() => setPropExpanded(false)}
              className="absolute top-3 right-3 z-[450] p-1.5 rounded-lg border border-zinc-700/60 bg-zinc-900/80 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* ── RIGHT: Propagation analysis panel ── */}
          <div className="w-[320px] flex-shrink-0 flex flex-col gap-3 overflow-y-auto">

            {/* Header */}
            <div className="flex items-center justify-between flex-shrink-0">
              <div>
                <div className="text-sm font-bold text-zinc-100 tracking-wide">Propagation Network</div>
                <div className="text-xs text-zinc-400 mt-0.5">Live OSINT · {toDisplayTime(lastUpdated)}</div>
              </div>
              <button
                type="button"
                onClick={() => setPropExpanded(false)}
                className="p-1.5 rounded-lg border border-zinc-700 bg-zinc-900/80 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Network stats grid */}
            <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 backdrop-blur-sm p-4 grid grid-cols-2 gap-3 flex-shrink-0">
              <div className="text-center">
                <div className="text-2xl font-bold text-zinc-100 tabular-nums">{visibleHotspots.length}</div>
                <div className="text-xs text-zinc-400 mt-0.5">Active Nodes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-violet-300 tabular-nums">{propagationLines.length}</div>
                <div className="text-xs text-zinc-400 mt-0.5">Propagation Edges</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-amber-300 tabular-nums">{escalatingCount}</div>
                <div className="text-xs text-zinc-400 mt-0.5">Escalating Nodes</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold tabular-nums" style={{ color: colorForRisk(globalRiskScore) }}>
                  {globalRiskScore}
                </div>
                <div className="text-xs text-zinc-400 mt-0.5">Network Risk</div>
              </div>
            </div>

            {/* Top propagation routes */}
            <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 backdrop-blur-sm p-4 flex-shrink-0">
              <div className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Top Propagation Routes</div>
              {propagationLines.length === 0 ? (
                <div className="text-[11px] text-zinc-400 text-center py-4">
                  No HIGH / CRITICAL nodes active — lower the risk threshold to reveal routes
                </div>
              ) : (
                <div className="space-y-2.5">
                  {propagationLines.map((line, idx) => {
                    const fromSpot = visibleHotspots.find(
                      (s) => Math.abs(s.lat - line.from[0]) < 0.001 && Math.abs(s.lng - line.from[1]) < 0.001,
                    );
                    const toSpot = visibleHotspots.find(
                      (s) => Math.abs(s.lat - line.to[0]) < 0.001 && Math.abs(s.lng - line.to[1]) < 0.001,
                    );
                    const fromRisk = fromSpot ? Math.round((fromSpot.avg_risk ?? fromSpot.confidence) * 100) : 0;
                    const toRisk = toSpot ? Math.round((toSpot.avg_risk ?? toSpot.confidence) * 100) : 0;
                    const avgRisk = Math.round((fromRisk + toRisk) / 2);
                    return (
                      <div key={idx} className="rounded-lg border border-zinc-800/80 bg-zinc-900/40 px-3 py-2.5">
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <span className="text-zinc-200 truncate">{fromSpot?.name ?? '?'}</span>
                          <span className="text-violet-400 flex-shrink-0">→</span>
                          <span className="text-zinc-200 truncate">{toSpot?.name ?? '?'}</span>
                        </div>
                        <div className="mt-1.5 h-0.5 rounded-full bg-zinc-800 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${avgRisk}%`, background: line.color }} />
                        </div>
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-400">
                          <span style={{ color: line.color }}>{avgRisk} avg risk</span>
                          <span>·</span>
                          <span>Weight {line.weight}</span>
                          <span>·</span>
                          <span>{fromSpot?.region ?? ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Node connectivity ranking */}
            <div className="rounded-xl border border-zinc-700/60 bg-zinc-900/60 backdrop-blur-sm p-4 flex-1 min-h-0">
              <div className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Node Connectivity · by Edges</div>
              <div className="space-y-2 overflow-y-auto">
                {[...visibleHotspots]
                  .map((spot) => ({
                    ...spot,
                    edgeCount: propagationLines.filter(
                      (l) =>
                        (Math.abs(l.from[0] - spot.lat) < 0.001 && Math.abs(l.from[1] - spot.lng) < 0.001) ||
                        (Math.abs(l.to[0] - spot.lat) < 0.001 && Math.abs(l.to[1] - spot.lng) < 0.001),
                    ).length,
                  }))
                  .sort((a, b) => b.edgeCount - a.edgeCount || (b.avg_risk ?? b.confidence) - (a.avg_risk ?? a.confidence))
                  .map((spot, i) => {
                    const riskScore = Math.round((spot.avg_risk ?? spot.confidence) * 100);
                    return (
                      <button
                        key={spot.id}
                        type="button"
                        onClick={() => { setSelectedId(spot.id); setPropExpanded(false); }}
                        className="w-full rounded-lg border border-zinc-800/80 bg-zinc-900/50 px-3 py-2.5 text-left hover:border-zinc-600 hover:bg-zinc-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400 font-mono w-4">#{i + 1}</span>
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: SEV[spot.severity].color }} />
                          <span className="text-[11px] text-zinc-200 flex-1 truncate">{spot.name}</span>
                          <span className="text-xs font-mono text-violet-300">{spot.edgeCount} edges</span>
                        </div>
                        <div className="mt-1 ml-5 text-xs text-zinc-400 flex items-center gap-1.5">
                          <span>{spot.region}</span>
                          <span>·</span>
                          <span style={{ color: colorForRisk(riskScore) }}>{riskScore} risk</span>
                          {spot.escalating && <span className="text-amber-400">↑</span>}
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* AI propagation insight */}
            <div className="rounded-xl border border-violet-500/20 bg-violet-950/20 px-4 py-3 flex-shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <BrainCircuit className="h-3.5 w-3.5 text-violet-400 flex-shrink-0" />
                <span className="text-xs text-violet-300 font-semibold uppercase tracking-wider">Propagation Insight</span>
              </div>
              <div className="text-[11px] text-zinc-300 leading-relaxed">
                {aiInsights[insightIndex % aiInsights.length]}
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default OSINTMap;
